import { createSelector } from 'reselect';
import { selectFeatureFlags } from 'yoda-site-components/lib/selectors/ContextSelector';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import filter from 'lodash/filter';
import map from 'lodash/map';
import forEach from 'lodash/forEach';
import uniqWith from 'lodash/uniqWith';
import uniq from 'lodash/uniq';
import some from 'lodash/some';
import compact from 'lodash/compact';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

const productDetails = (state) => state.productDetails;

const productKA = (productData) => {
    const knowledgeAssistants = uniqWith(
        compact(flatten(map(productData, 'knowledgeAssistants'))),
        isEqual
    );
    return knowledgeAssistants;
};

const productSG = (productData) => {
    const sizeGuide = uniqWith(compact(flatten(map(productData, 'sizeGuide'))), isEqual);
    return sizeGuide;
};

// To select sideGuide over size chart. Introduced as part of data driven size chart feature.
const selectKAWithSizeGuide = (product = {}) => {
    const { knowledgeAssistants: ka = [], sizeGuide: sg = {} } = product;
    let knowledgeAssistants = [];
    let sizeGuide = {};
    if (!isEmpty(sg) && some(ka, { kaType: 'Size Chart' })) {
        knowledgeAssistants = filter(ka, (productKa) => productKa.kaType !== 'Size Chart');
        sizeGuide = sg;
    } else {
        knowledgeAssistants = ka;
        sizeGuide = sg;
    }
    return { knowledgeAssistants, sizeGuide };
};

const productKAWithSizeGuide = (productData = []) => {
    const product = productData.map(selectKAWithSizeGuide);
    const knowledgeAssistants = productKA(product).filter((ka) => Object.keys(ka).length !== 0);
    const sizeGuide = productSG(product).filter((sg) => Object.keys(sg).length !== 0);
    return { knowledgeAssistants, sizeGuide };
};

const productGI = (product, bundleData) => {
    let defaultLot = [];
    let availableWarranties = [];
    /* istanbul ignore next */
    const lots = bundleData && bundleData.lots ? bundleData.lots : [];
    forEach(product, ({ lots: productLots = [] } = {}) => {
        defaultLot = compact([...defaultLot, filter(productLots, { isDefault: true })[0]]);
    });
    const type = uniq(compact(flatten(map(defaultLot, 'type'))));
    const tags = uniq(compact(flatten(map(defaultLot, 'tags'))));
    forEach(defaultLot, ({ warranties = [], type: productType = '' }) => {
        availableWarranties =
            warranties && warranties.length
                ? [...availableWarranties, productType]
                : [...availableWarranties];
    });
    availableWarranties = uniq(compact(flatten(availableWarranties)));
    return [].concat({ ...lots[0], tags, type, availableWarranties });
};

const selectedProductInfo = createSelector(
    [productDetails, selectFeatureFlags],
    (bundleData, featureFlags) => {
        const product =
            bundleData && bundleData.products ? map(bundleData.products, 'product') : [];
        const KA = productKA(product);
        const GI = productGI(product, bundleData);
        const KA_SG = get(featureFlags, 'disableDataSizeChart', false)
            ? {}
            : productKAWithSizeGuide(product);
        const productInfo = {
            ...bundleData,
            knowledgeAssistants: KA,
            lots: GI,
            kaWithSizeGuide: KA_SG,
        };
        return productInfo;
    }
);

export default selectedProductInfo;
